/**
 * Trip money, pure (ADR 0030, plan §5.8, TD-4.2, 4.5, 4.14, 4.15, 4.18). Decimal.js in, decimal
 * strings out (CLAUDE.md invariant 2). Nothing here reads a clock or a database: the API loads the
 * rows, calls these, and writes the result in one transaction.
 *
 *  - `tripAdvanceLimit` / `advanceWithinPolicy`: the most a trip advance may be (the policy's
 *    `maxAdvancePct` of the estimate and `maxAdvanceAmount`, the lower one wins), so a request
 *    within it is auto-approved (TD-4.14).
 *  - `planSettlementNetting`: the batch net-off of one approved settlement round. The order is
 *    fixed (EC-36, EC-37): the TRIP'S OWN paid-out advances first, oldest first; then, only when the
 *    caller asks, the traveller's OTHER overdue advances in the same currency, oldest first. An
 *    approved-but-unpaid advance of the trip is cancelled at the main round (EC-36: the claims are
 *    paid directly instead). Currencies never mix: a USD claim only nets a USD advance.
 *  - `summariseTripMoney`: the money panel, one row per currency, never summed across currencies.
 *
 * ONE CLAIM, ONE ADVANCE. `ExpenseClaim` records a single `advanceId` + `advanceAppliedAmount`, so
 * a claim draws on at most one advance: when an advance runs out part-way through a claim, the
 * rest of that claim is payable and the next claim starts on the next advance. With one advance per
 * trip (the normal case) this nets the most it can; with several, a little can stay unnetted, and
 * `totals` reports it so nothing is lost (the employee is paid it and the advance stays open for
 * recovery).
 */

import { Decimal } from 'decimal.js';
import type { ExpenseAdvanceStatus, TravelSettlementKind } from '../enums.js';
import { ValidationError } from '../errors.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ZERO = new Decimal(0);
const HUNDRED = new Decimal(100);

/**
 * A money decimal as the wire carries it: two places, or four when the value really has more
 * (the columns are `numeric(18,4)`). Never rounds a value away.
 */
export function toMoneyString(value: Decimal.Value): string {
  const d = new Decimal(value);
  // A zero reached by negating (or by -0 input) must not print as "-0.00".
  if (d.isZero()) return '0.00';
  return d.decimalPlaces() <= 2 ? d.toFixed(2) : d.toDecimalPlaces(4, Decimal.ROUND_HALF_UP).toFixed(4);
}

function amountOf(value: string | null | undefined, label: string): Decimal {
  if (value === null || value === undefined) return ZERO;
  let d: Decimal;
  try {
    d = new Decimal(value);
  } catch {
    throw new ValidationError(`${label} is not a number`);
  }
  if (!d.isFinite()) throw new ValidationError(`${label} is not a number`);
  return d;
}

function nonNegative(value: string | null | undefined, label: string): Decimal {
  const d = amountOf(value, label);
  if (d.lt(0)) throw new ValidationError(`${label} cannot be negative`);
  return d;
}

function byCurrency<T>(map: Map<string, T>, currency: string, init: () => T): T {
  let entry = map.get(currency);
  if (entry === undefined) {
    entry = init();
    map.set(currency, entry);
  }
  return entry;
}

/** Sorted currency keys, with `primary` (the trip's currency) first when present. */
function orderedCurrencies(keys: Iterable<string>, primary?: string | null): string[] {
  const list = [...new Set(keys)].sort();
  if (primary && list.includes(primary)) return [primary, ...list.filter((c) => c !== primary)];
  return list;
}

// ---------------------------------------------------------------------------
// Advance limit (TD-4.2, TD-4.14)
// ---------------------------------------------------------------------------

export interface TripAdvanceLimitInput {
  /** The trip's approved estimate, in the trip's currency. Null: no estimate. */
  estimatedAmount: string | null;
  /** Percent, 0–100. Null: no percentage cap. */
  maxAdvancePct: string | null;
  /** In the policy's currency (the trip's). Null: no amount cap. */
  maxAdvanceAmount: string | null;
}

/**
 * The most an advance for the trip may be: the lower of `maxAdvancePct` × estimate and
 * `maxAdvanceAmount`. Null means uncapped by the travel policy (the expense module's own advance
 * ceilings still apply). A percentage cap with no estimate caps at zero: nothing is known to be
 * spent, so an auto-approval has nothing to stand on.
 */
export function tripAdvanceLimit(input: TripAdvanceLimitInput): string | null {
  const caps: Decimal[] = [];
  if (input.maxAdvancePct !== null) {
    const pct = nonNegative(input.maxAdvancePct, 'maxAdvancePct');
    const estimate = nonNegative(input.estimatedAmount, 'estimatedAmount');
    caps.push(estimate.mul(pct).div(HUNDRED).toDecimalPlaces(2, Decimal.ROUND_DOWN));
  }
  if (input.maxAdvanceAmount !== null) caps.push(nonNegative(input.maxAdvanceAmount, 'maxAdvanceAmount'));
  if (caps.length === 0) return null;
  return toMoneyString(Decimal.min(...caps));
}

/** True when `amount` is positive and within `limit` (null limit: always within). */
export function advanceWithinPolicy(amount: string, limit: string | null): boolean {
  const a = amountOf(amount, 'amount');
  if (!a.gt(0)) return false;
  return limit === null || a.lte(amountOf(limit, 'limit'));
}

// ---------------------------------------------------------------------------
// Batch net-off (TD-4.5, TD-4.15, EC-36, EC-37)
// ---------------------------------------------------------------------------

/** One APPROVED expense of the round (its approved amount; rejected lines are left out). */
export interface NettingClaim {
  id: string;
  currency: string;
  /** The approved amount (`approvedAmount`, else `amount`). */
  amount: string;
  /** `YYYY-MM-DD`; claims net in date order, then by id. */
  expenseDate: string;
}

/** One of the traveller's advances, as loaded (the trip's own and, for EC-37, the others). */
export interface NettingAdvance {
  id: string;
  currency: string;
  status: ExpenseAdvanceStatus;
  /** Null until paid out. */
  outstandingAmount: string | null;
  travelRequestId: string | null;
  /** ISO instant; orders the advances, oldest first. Null sorts last. */
  disbursedAt: string | null;
  /** Past its settle-by date (the API decides, in the traveller's timezone). */
  overdue: boolean;
}

export interface NettingInput {
  travelRequestId: string;
  round: TravelSettlementKind;
  claims: readonly NettingClaim[];
  advances: readonly NettingAdvance[];
  /**
   * EC-37: after the trip's own advances, also net the traveller's other OVERDUE paid-out advances
   * in the same currency. Off by default: the money then goes to the employee and the other
   * advance follows its own recovery.
   */
  includeOtherOverdueAdvances?: boolean;
}

export interface NettingAllocation {
  claimId: string;
  currency: string;
  amount: string;
  /** The advance this claim nets against; null when nothing was netted. */
  advanceId: string | null;
  /** What the advance covers. */
  appliedAmount: string;
  /** What is left to pay the employee (`amount − appliedAmount`). */
  payableAmount: string;
}

export interface NettingAdvanceResult {
  advanceId: string;
  currency: string;
  /** The trip's own advance (false: another overdue advance, EC-37). */
  ownTrip: boolean;
  outstandingBefore: string;
  applied: string;
  outstandingAfter: string;
  /** Nothing left: the advance moves to `settled`. */
  settled: boolean;
}

export interface NettingCurrencyTotal {
  currency: string;
  approved: string;
  netted: string;
  payable: string;
  /** What is still outstanding on the TRIP'S OWN advances after this round: recoverable unless later claims absorb it. */
  tripAdvanceOutstanding: string;
}

export interface NettingPlan {
  allocations: NettingAllocation[];
  /** Only advances that took something, in the order they were drawn on. */
  advances: NettingAdvanceResult[];
  /**
   * The trip's own advances that were approved (or still pending) but never paid out: cancelled at
   * the main or supplementary round (EC-36). Empty for a pre-trip round, which comes before the trip.
   */
  cancelAdvanceIds: string[];
  totals: NettingCurrencyTotal[];
}

const OPEN_UNPAID: readonly ExpenseAdvanceStatus[] = ['pending', 'approved'];

function disbursedOrder(a: NettingAdvance, b: NettingAdvance): number {
  if (a.disbursedAt !== b.disbursedAt) {
    if (a.disbursedAt === null) return 1;
    if (b.disbursedAt === null) return -1;
    return a.disbursedAt < b.disbursedAt ? -1 : 1;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function planSettlementNetting(input: NettingInput): NettingPlan {
  const ids = new Set<string>();
  for (const claim of input.claims) {
    if (ids.has(claim.id)) throw new ValidationError(`Claim ${claim.id} is listed twice`);
    ids.add(claim.id);
    nonNegative(claim.amount, `Claim ${claim.id} amount`);
  }

  const isOwn = (a: NettingAdvance): boolean => a.travelRequestId === input.travelRequestId;
  const paidOut = (a: NettingAdvance): boolean =>
    a.status === 'disbursed' && nonNegative(a.outstandingAmount, `Advance ${a.id} outstanding`).gt(0);

  const own = input.advances.filter((a) => isOwn(a) && paidOut(a)).sort(disbursedOrder);
  const others = input.includeOtherOverdueAdvances
    ? input.advances.filter((a) => !isOwn(a) && a.overdue && paidOut(a)).sort(disbursedOrder)
    : [];
  const queue = [...own, ...others];

  const remaining = new Map<string, Decimal>(
    queue.map((a) => [a.id, amountOf(a.outstandingAmount, `Advance ${a.id} outstanding`)]),
  );
  const applied = new Map<string, Decimal>();

  const claims = [...input.claims].sort((a, b) =>
    a.expenseDate !== b.expenseDate ? (a.expenseDate < b.expenseDate ? -1 : 1) : a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  );

  const allocations: NettingAllocation[] = [];
  const totals = new Map<string, { approved: Decimal; netted: Decimal; payable: Decimal }>();

  for (const claim of claims) {
    const amount = new Decimal(claim.amount);
    let advanceId: string | null = null;
    let take = ZERO;
    if (amount.gt(0)) {
      const advance = queue.find(
        (a) => a.currency === claim.currency && (remaining.get(a.id) ?? ZERO).gt(0),
      );
      if (advance) {
        const left = remaining.get(advance.id) ?? ZERO;
        take = Decimal.min(left, amount);
        remaining.set(advance.id, left.sub(take));
        applied.set(advance.id, (applied.get(advance.id) ?? ZERO).add(take));
        advanceId = advance.id;
      }
    }
    const payable = amount.sub(take);
    allocations.push({
      claimId: claim.id,
      currency: claim.currency,
      amount: toMoneyString(amount),
      advanceId,
      appliedAmount: toMoneyString(take),
      payableAmount: toMoneyString(payable),
    });
    const t = byCurrency(totals, claim.currency, () => ({ approved: ZERO, netted: ZERO, payable: ZERO }));
    t.approved = t.approved.add(amount);
    t.netted = t.netted.add(take);
    t.payable = t.payable.add(payable);
  }

  const advances: NettingAdvanceResult[] = queue
    .filter((a) => applied.has(a.id))
    .map((a) => {
      const before = amountOf(a.outstandingAmount, 'outstanding');
      const after = remaining.get(a.id) ?? ZERO;
      return {
        advanceId: a.id,
        currency: a.currency,
        ownTrip: isOwn(a),
        outstandingBefore: toMoneyString(before),
        applied: toMoneyString(applied.get(a.id) ?? ZERO),
        outstandingAfter: toMoneyString(after),
        settled: after.isZero(),
      };
    });

  const cancelAdvanceIds =
    input.round === 'pre_trip'
      ? []
      : input.advances
          .filter((a) => isOwn(a) && OPEN_UNPAID.includes(a.status))
          .map((a) => a.id)
          .sort();

  const ownOutstanding = new Map<string, Decimal>();
  for (const a of own) {
    const cur = ownOutstanding.get(a.currency) ?? ZERO;
    ownOutstanding.set(a.currency, cur.add(remaining.get(a.id) ?? ZERO));
  }

  const currencies = orderedCurrencies([...totals.keys(), ...ownOutstanding.keys()]);
  return {
    allocations,
    advances,
    cancelAdvanceIds,
    totals: currencies.map((currency) => {
      const t = totals.get(currency) ?? { approved: ZERO, netted: ZERO, payable: ZERO };
      return {
        currency,
        approved: toMoneyString(t.approved),
        netted: toMoneyString(t.netted),
        payable: toMoneyString(t.payable),
        tripAdvanceOutstanding: toMoneyString(ownOutstanding.get(currency) ?? ZERO),
      };
    }),
  };
}

// ---------------------------------------------------------------------------
// The money panel (TD-4.10 read side)
// ---------------------------------------------------------------------------

export interface MoneySummaryAdvance {
  currency: string;
  status: ExpenseAdvanceStatus;
  requestedAmount: string;
  outstandingAmount: string | null;
}

/** `status` is `ExpenseClaim.status`: draft | pending | approved | rejected | settled | withdrawn. */
export interface MoneySummaryClaim {
  currency: string;
  status: string;
  amount: string;
  approvedAmount: string | null;
  advanceAppliedAmount: string | null;
}

/** A company-paid booking (paid by the traveller is a claim, not this). */
export interface MoneySummaryBooking {
  currency: string | null;
  /** `TravelBooking.status`: only `confirmed` and `cancelled` carry money. */
  status: string;
  amount: string | null;
  /** Counted back only once received. */
  refundAmount: string | null;
  refundReceived: boolean;
}

export interface TripMoneyTotals {
  currency: string;
  /** Asked for and not yet paid out (pending or approved). */
  advanceRequested: string;
  /** Paid out to the traveller, ever (disbursed, settled or written off). */
  advancePaid: string;
  /** Still with the traveller on paid-out advances. */
  advanceOutstanding: string;
  /** Claims not yet submitted. */
  claimDraft: string;
  /** Submitted, waiting for a decision (alone or in a settlement round). */
  claimPending: string;
  /** Approved and not yet paid or netted. */
  claimApproved: string;
  /** Paid or netted. */
  claimSettled: string;
  claimRejected: string;
  /** Of the settled claims, what the advance covered. */
  nettedFromAdvance: string;
  /** Bookings the company paid for the trip (refunds received taken off). */
  companyPaid: string;
  /**
   * Signed: (approved + pending claims) − outstanding advance. Positive is owed to the traveller,
   * negative is owed back by them, if every pending claim is approved as claimed.
   */
  netPosition: string;
  netPayable: string;
  netRecoverable: string;
}

export interface TripMoneyInput {
  /** The trip's currency, listed first. */
  primaryCurrency?: string | null;
  advances: readonly MoneySummaryAdvance[];
  claims: readonly MoneySummaryClaim[];
  companyBookings?: readonly MoneySummaryBooking[];
}

interface Acc {
  advanceRequested: Decimal;
  advancePaid: Decimal;
  advanceOutstanding: Decimal;
  claimDraft: Decimal;
  claimPending: Decimal;
  claimApproved: Decimal;
  claimSettled: Decimal;
  claimRejected: Decimal;
  nettedFromAdvance: Decimal;
  companyPaid: Decimal;
}

const emptyAcc = (): Acc => ({
  advanceRequested: ZERO,
  advancePaid: ZERO,
  advanceOutstanding: ZERO,
  claimDraft: ZERO,
  claimPending: ZERO,
  claimApproved: ZERO,
  claimSettled: ZERO,
  claimRejected: ZERO,
  nettedFromAdvance: ZERO,
  companyPaid: ZERO,
});

const PAID_OUT: readonly ExpenseAdvanceStatus[] = ['disbursed', 'settled', 'written_off'];

/** One row per currency that has any money in it; the trip's currency first. */
export function summariseTripMoney(input: TripMoneyInput): TripMoneyTotals[] {
  const acc = new Map<string, Acc>();

  for (const a of input.advances) {
    const row = byCurrency(acc, a.currency, emptyAcc);
    const requested = nonNegative(a.requestedAmount, 'requestedAmount');
    if (OPEN_UNPAID.includes(a.status)) row.advanceRequested = row.advanceRequested.add(requested);
    if (PAID_OUT.includes(a.status)) row.advancePaid = row.advancePaid.add(requested);
    if (a.status === 'disbursed') {
      row.advanceOutstanding = row.advanceOutstanding.add(nonNegative(a.outstandingAmount, 'outstandingAmount'));
    }
  }

  for (const c of input.claims) {
    if (c.status === 'withdrawn') continue;
    const row = byCurrency(acc, c.currency, emptyAcc);
    const amount = nonNegative(c.amount, 'amount');
    const approved = c.approvedAmount === null ? amount : nonNegative(c.approvedAmount, 'approvedAmount');
    switch (c.status) {
      case 'draft':
        row.claimDraft = row.claimDraft.add(amount);
        break;
      case 'pending':
        row.claimPending = row.claimPending.add(amount);
        break;
      case 'approved':
        row.claimApproved = row.claimApproved.add(approved);
        break;
      case 'settled':
        row.claimSettled = row.claimSettled.add(approved);
        row.nettedFromAdvance = row.nettedFromAdvance.add(
          nonNegative(c.advanceAppliedAmount, 'advanceAppliedAmount'),
        );
        break;
      case 'rejected':
        row.claimRejected = row.claimRejected.add(amount);
        break;
      default:
        break;
    }
  }

  for (const b of input.companyBookings ?? []) {
    if (b.currency === null || b.amount === null) continue;
    if (b.status !== 'confirmed' && b.status !== 'cancelled') continue;
    const row = byCurrency(acc, b.currency, emptyAcc);
    let spent = nonNegative(b.amount, 'booking amount');
    if (b.status === 'cancelled' && b.refundReceived) {
      spent = Decimal.max(ZERO, spent.sub(nonNegative(b.refundAmount, 'refundAmount')));
    }
    row.companyPaid = row.companyPaid.add(spent);
  }

  return orderedCurrencies(acc.keys(), input.primaryCurrency).map((currency) => {
    const r = acc.get(currency) ?? emptyAcc();
    const net = r.claimApproved.add(r.claimPending).sub(r.advanceOutstanding);
    return {
      currency,
      advanceRequested: toMoneyString(r.advanceRequested),
      advancePaid: toMoneyString(r.advancePaid),
      advanceOutstanding: toMoneyString(r.advanceOutstanding),
      claimDraft: toMoneyString(r.claimDraft),
      claimPending: toMoneyString(r.claimPending),
      claimApproved: toMoneyString(r.claimApproved),
      claimSettled: toMoneyString(r.claimSettled),
      claimRejected: toMoneyString(r.claimRejected),
      nettedFromAdvance: toMoneyString(r.nettedFromAdvance),
      companyPaid: toMoneyString(r.companyPaid),
      netPosition: toMoneyString(net),
      netPayable: toMoneyString(Decimal.max(ZERO, net)),
      netRecoverable: toMoneyString(Decimal.max(ZERO, net.neg())),
    };
  });
}
